/**
 * AI Scribe V2 — at-rest encryption for the session snapshot
 *
 * AES-GCM via the native Web Crypto API, so the transcript is never written to
 * `sessionStorage` in plaintext. The key is derived per appointment (PBKDF2
 * over a fixed passphrase + a salt derived from the session key) and cached, so
 * each write only pays for a fresh random IV, not another derivation.
 *
 * This is defence in depth — anything executing on the page can re-derive the
 * same key, so it is not a boundary against XSS or a determined local user.
 */

import {
  SCRIBE_SESSION_CRYPTO_PASSPHRASE,
  SCRIBE_SESSION_IV_BYTES,
  SCRIBE_SESSION_PBKDF2_ITERATIONS,
  SCRIBE_SESSION_SALT_BYTES,
} from "./constants";

const ENC = new TextEncoder();
const DEC = new TextDecoder();

/** salt and ciphertext are chunked to base64 so a long transcript never blows the call stack. */
const toBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
};

const fromBase64 = (value: string): Uint8Array => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const deriveSalt = async (sessionKey: string): Promise<Uint8Array> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    ENC.encode(`${SCRIBE_SESSION_CRYPTO_PASSPHRASE}:${sessionKey}`)
  );
  return new Uint8Array(digest).slice(0, SCRIBE_SESSION_SALT_BYTES);
};

const deriveKey = async (sessionKey: string): Promise<CryptoKey> => {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    ENC.encode(SCRIBE_SESSION_CRYPTO_PASSPHRASE),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: await deriveSalt(sessionKey),
      iterations: SCRIBE_SESSION_PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

/** PBKDF2 is expensive, so the derived key is reused for every write of a session. */
const keyCache = new Map<string, Promise<CryptoKey>>();
const getKey = (sessionKey: string): Promise<CryptoKey> => {
  const cached = keyCache.get(sessionKey);
  if (cached) return cached;
  const fresh = deriveKey(sessionKey);
  keyCache.set(sessionKey, fresh);
  return fresh;
};

interface ScribeCipherEnvelope {
  /** base64 initialisation vector, unique per write */
  i: string;
  /** base64 AES-GCM ciphertext (payload + auth tag) */
  d: string;
}

export const encryptScribePayload = async (
  sessionKey: string,
  plaintext: string
): Promise<string> => {
  const key = await getKey(sessionKey);
  const iv = crypto.getRandomValues(new Uint8Array(SCRIBE_SESSION_IV_BYTES));
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    ENC.encode(plaintext)
  );
  const envelope: ScribeCipherEnvelope = {
    i: toBase64(iv),
    d: toBase64(new Uint8Array(cipher)),
  };
  return JSON.stringify(envelope);
};

export const decryptScribePayload = async (
  sessionKey: string,
  raw: string
): Promise<string | null> => {
  try {
    const envelope = JSON.parse(raw) as ScribeCipherEnvelope;
    if (!envelope?.i || !envelope?.d) return null;
    const key = await getKey(sessionKey);
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(envelope.i) },
      key,
      fromBase64(envelope.d)
    );
    return DEC.decode(plain);
  } catch {
    return null;
  }
};
